"use client";

import type { Answers } from "@fieldtripjs/core";
import { useFlow } from "@fieldtripjs/react";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { type ReactElement, useEffect, useMemo, useRef, useState } from "react";
import type { AddressSuggestion } from "@/features/geo/shared/address-types";
import { findPostalCode } from "@/features/geo/shared/postal-codes";
import { parseLeadResponse, taskPagePath } from "@/features/leads/shared/response";
import {
  audienceIn,
  audienceSwitch,
  buildLeadPayload,
  linkAnswers,
  makeQuoteFlow,
  type QuoteFlow,
  type QuoteState,
  STEP_PHASES,
  type StepId,
  startState,
} from "@/features/quote/shared/flow";
import type { Audience, InitialFields } from "@/features/quote/shared/quote-config";
import { EMAIL_RE } from "@/features/quote/shared/validation";
import { holdMatchingOverlay, MatchingOverlay } from "@/features/quote/ui/MatchingOverlay";
import { PrefillNotice } from "@/features/quote/ui/PrefillNotice";
import { ProgressBar, wizardPct } from "@/features/quote/ui/ProgressBar";
import { AddressStep } from "@/features/quote/ui/steps/AddressStep";
import { AudienceStep } from "@/features/quote/ui/steps/AudienceStep";
import { BusinessStep } from "@/features/quote/ui/steps/BusinessStep";
import { CallTimeStep } from "@/features/quote/ui/steps/CallTimeStep";
import { ContactStep } from "@/features/quote/ui/steps/ContactStep";
import { FrequencyStep } from "@/features/quote/ui/steps/FrequencyStep";
import { PostalStep } from "@/features/quote/ui/steps/PostalStep";
import { PreferencesStep } from "@/features/quote/ui/steps/PreferencesStep";
import { PriorityStep } from "@/features/quote/ui/steps/PriorityStep";
import { PropertyStep } from "@/features/quote/ui/steps/PropertyStep";
import { SqmStep } from "@/features/quote/ui/steps/SqmStep";
import { StartStep } from "@/features/quote/ui/steps/StartStep";
import { SubtypeStep } from "@/features/quote/ui/steps/SubtypeStep";
import { TaskStep } from "@/features/quote/ui/steps/TaskStep";
import { TimingStep } from "@/features/quote/ui/steps/TimingStep";
import { useWizardHeightLock } from "@/features/quote/ui/useWizardHeightLock";
import { WizardSidebar } from "@/features/quote/ui/WizardSidebar";
import { WizardStepShell } from "@/features/quote/ui/WizardStepShell";
import {
  type BbrPrefilled,
  type StepAnswers,
  WizardProvider,
} from "@/features/quote/ui/wizard-context";
import type { BbrEnrichedData } from "@/integrations/bbr-codes";
import { generateEventId, trackEvent } from "@/shared/analytics/meta-pixel";
import { useUtmParams } from "@/shared/analytics/useUtmParams";
import { isValidDanishNational } from "@/shared/lib/phone";

function formatAddressLine(address: AddressSuggestion): string {
  const a = address.adresse;
  const unit = [a.etage ? `${a.etage}.` : null, a.dør].filter(Boolean).join(" ");
  const street = `${a.vejnavn} ${a.husnr}${unit ? `, ${unit}` : ""}`;
  return `${street}, ${a.postnr} ${a.postnrnavn}`;
}

// Floor for the hero card's step area (px) so short choice steps don't look
// cramped. Taller steps (address/contact) drive their own height, animated
// smoothly by the height lock.
const HERO_STEP_MIN_H = 460;

const NO_BBR: BbrPrefilled = { propertyType: false, floor: false, sqm: false };

// Each step id maps to its own component; components read state and handlers
// via WizardProvider rather than props.
const STEP_COMPONENTS: Record<StepId, () => ReactElement> = {
  audience: AudienceStep,
  business: BusinessStep,
  task: TaskStep,
  subtype: SubtypeStep,
  address: AddressStep,
  property: PropertyStep,
  sqm: SqmStep,
  frequency: FrequencyStep,
  start: StartStep,
  timing: TimingStep,
  preferences: PreferencesStep,
  priority: PriorityStep,
  postal: PostalStep,
  callTime: CallTimeStep,
  contact: ContactStep,
};

export function QuoteWizard({
  initial = {},
  variant = "default",
  addressLookupEnabled = false,
  isRepeatTask = false,
}: {
  initial?: Partial<InitialFields>;
  variant?: "default" | "hero";
  // Arrived from the "Har du en opgave mere?" CTA (?igen=1). Gated on its own
  // flag rather than "any prefill param is present": marketing and price-
  // calculator deep-links also prefill, but for them a notice about "din
  // forrige opgave" would be wrong.
  isRepeatTask?: boolean;
  // Server-side flag mirroring isAddressLookupEnabled() (DATAFORDELER_API_KEY).
  // When false, the wizard skips the address step and falls back to the
  // postal-code-only flow - we never hit /api/address/* without a key.
  addressLookupEnabled?: boolean;
}) {
  const isHero = variant === "hero";
  const initialAudience = initial.audience ?? null;
  const audienceLocked = isHero && initialAudience !== null;
  const posthog = usePostHog();
  const router = useRouter();
  const utmParams = useUtmParams();
  const lockedAudience = audienceLocked ? initialAudience : null;
  const flow = useMemo(
    () => makeQuoteFlow({ addressLookup: addressLookupEnabled, lockedAudience }),
    [addressLookupEnabled, lockedAudience],
  );
  // The repeat-task flow stays on the first step so PrefillNotice can explain what it carried over.
  const [initialFlowState] = useState(() =>
    startState(flow, linkAnswers(initial), { fromStart: isRepeatTask }),
  );
  // Read by handlers that run after an await, when the render they closed over is stale.
  const latest = useRef(initialFlowState);
  const wizard = useFlow(flow, {
    initialState: initialFlowState,
    onChange: (next) => {
      const previous = latest.current;
      latest.current = next;
      if (next.current === previous.current) return;
      setDirection(next.history.length >= previous.history.length ? 1 : -1);
      posthog?.capture("wizard_step_completed", {
        wizard: "tilbud",
        from_step: previous.history.length + 1,
        to_step: next.history.length + 1,
        from_step_id: previous.current,
        to_step_id: next.current,
        audience: audienceIn(next, lockedAudience),
      });
      scrollWizardIntoView();
    },
  });
  const state = wizard.state;
  const audience = audienceIn(state, lockedAudience);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Height lock (grow-only + hero floor) - see useWizardHeightLock. Keeps the
  // card from jumping when steps with different content heights switch.
  const {
    rootRef,
    stepsRef,
    lockStyle,
    scrollIntoView: scrollWizardIntoView,
  } = useWizardHeightLock({ floor: isHero ? HERO_STEP_MIN_H : 0 });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [matchFound, setMatchFound] = useState(false);
  const [bbrLoading, setBbrLoading] = useState(false);
  const [bbrError, setBbrError] = useState<string | null>(null);
  const [bbrPrefilled, setBbrPrefilled] = useState<BbrPrefilled>(NO_BBR);
  const bbrData = useRef<BbrEnrichedData | null>(null);
  // Synchronous gate against double-clicks: `submitting` is state and only
  // takes effect on the next render. The server is still the source of truth.
  const inFlight = useRef(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: fire once on mount
  useEffect(() => {
    posthog?.capture("wizard_started", {
      wizard: "tilbud",
      audience: initialAudience,
      // Lets us measure how many second tasks the thank-you CTA actually produces.
      is_repeat_task: isRepeatTask,
      // >1 means a deep-link pre-answered steps — how far it got the customer.
      initial_step: initialFlowState.history.length + 1,
    });
  }, []);

  function answer<K extends StepId>(step: K, answers: StepAnswers<K>) {
    wizard.setInputs(answers, step);
  }

  function answersOf<K extends StepId>(step: K): StepAnswers<K> {
    return (state.inputs[step] ?? {}) as StepAnswers<K>;
  }

  function apply(answers: Answers<QuoteFlow>) {
    for (const [step, inputs] of Object.entries(answers)) {
      wizard.setInputs(inputs ?? {}, step as StepId);
    }
  }

  function selectAudience(next: Audience) {
    const changed = next !== audience;
    apply(audienceSwitch(state, next));
    if (!changed) return;
    dropBbrValues();
    if (bbrData.current) prefillFromBbr(bbrData.current, latest.current);
  }

  function next() {
    void wizard.next();
  }

  // Any later answer, Next or Back replaces the state, so only the last unanswered click advances.
  function autoAdvance(delay = 250) {
    const from = latest.current;
    setTimeout(() => {
      if (latest.current === from) next();
    }, delay);
  }

  function setFieldError(field: string, error: string | null) {
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (error) next[field] = error;
      else delete next[field];
      return next;
    });
  }

  function validateEmailField(value: string): string | null {
    const v = value.trim();
    if (!v) return null;
    if (!EMAIL_RE.test(v)) return "Indtast en gyldig emailadresse";
    return null;
  }

  function validatePhoneField(value: string): string | null {
    const v = value.trim();
    if (!v) return null;
    if (!isValidDanishNational(v)) return "Indtast et gyldigt dansk telefonnummer (8 cifre)";
    return null;
  }

  async function handleAddressSelect(address: AddressSuggestion) {
    dropBbrValues();
    bbrData.current = null;
    setBbrLoading(true);
    setBbrError(null);
    // Optimistic: set postalCode/streetAddress right away so the Next button
    // is active even if the BBR lookup takes a second.
    answer("address", {
      address,
      addressSkipped: false,
      streetAddress: formatAddressLine(address),
    });
    if (address.adresse.postnr) answer("postal", { postalCode: address.adresse.postnr });
    // A lookup that returns after the customer reset, skipped or picked another address is stale.
    const stillChosen = () => latest.current.inputs.address?.address === address;

    try {
      const sp = new URLSearchParams({
        id: address.adresse.id,
        vejnavn: address.adresse.vejnavn,
        husnr: address.adresse.husnr,
        postnr: address.adresse.postnr,
        postnrnavn: address.adresse.postnrnavn,
        kommunekode: address.adresse.kommunekode,
      });
      if (address.adresse.etage) sp.set("etage", address.adresse.etage);
      if (address.adresse.dør) sp.set("doer", address.adresse.dør);
      const res = await fetch(`/api/address/lookup?${sp.toString()}`);
      if (!stillChosen()) return;
      if (!res.ok) {
        setBbrError("Vi kunne ikke hente boligoplysninger - udfyld manuelt nedenfor.");
        return;
      }
      const data = (await res.json()) as BbrEnrichedData;
      if (!stillChosen()) return;
      bbrData.current = data;
      answer("address", { streetAddress: data.display.fullText });
      if (data.display.postnr) answer("postal", { postalCode: data.display.postnr });
      prefillFromBbr(data, latest.current);
      posthog?.capture("bbr_lookup", {
        adgangsadresse_id: data.adgangsadresseId,
        has_property_type: Boolean(data.privat.propertyType),
        has_business: Boolean(data.erhverv.business),
        has_sqm: Boolean(data.privat.squareMeters || data.erhverv.squareMeters),
      });
    } catch (err) {
      console.error("BBR-opslag fejlede:", err);
      if (stillChosen())
        setBbrError("Vi kunne ikke hente boligoplysninger - udfyld manuelt nedenfor.");
    } finally {
      if (stillChosen()) setBbrLoading(false);
    }
  }

  // BBR only fills what the customer hasn't answered on the steps ahead.
  function prefillFromBbr(data: BbrEnrichedData, current: QuoteState) {
    const isPrivat = audienceIn(current, lockedAudience) === "privat";
    const property = current.inputs.property ?? {};
    const sqmBbr = isPrivat ? data.privat.squareMeters : data.erhverv.squareMeters;
    const filled = { ...NO_BBR };
    if (isPrivat && data.privat.propertyType && !property.propertyType) {
      answer("property", { propertyType: data.privat.propertyType });
      filled.propertyType = true;
    }
    if (isPrivat && data.privat.floor && !property.floor) {
      answer("property", { floor: data.privat.floor });
      filled.floor = true;
    }
    if (sqmBbr && !current.inputs.sqm?.squareMeters) {
      answer("sqm", { squareMeters: String(sqmBbr) });
      filled.sqm = true;
    }
    setBbrPrefilled(filled);
  }

  // Untouched BBR answers describe the old address, so they leave with it.
  function dropBbrValues() {
    if (bbrPrefilled.propertyType) answer("property", { propertyType: undefined });
    if (bbrPrefilled.floor) answer("property", { floor: undefined });
    if (bbrPrefilled.sqm) answer("sqm", { squareMeters: undefined });
    setBbrPrefilled(NO_BBR);
  }

  // The postal code the address filled in goes too, unless the customer has changed it since.
  function dropAddressAnswers() {
    const postnr = answersOf("address").address?.adresse.postnr;
    if (postnr && answersOf("postal").postalCode === postnr) {
      answer("postal", { postalCode: undefined });
    }
    dropBbrValues();
    bbrData.current = null;
    setBbrLoading(false);
  }

  function handleSkipAddress() {
    dropAddressAnswers();
    answer("address", { address: undefined, addressSkipped: true, streetAddress: "" });
    setBbrError(null);
    posthog?.capture("address_skipped", { audience });
    autoAdvance(0);
  }

  function handleResetAddress() {
    dropAddressAnswers();
    answer("address", { address: undefined, addressSkipped: false, streetAddress: "" });
    setBbrError(null);
  }

  const isPrivat = audience === "privat";
  const isBusiness = audience === "erhverv";
  const activeStepId: StepId = wizard.step;
  const stepNumber = state.history.length + 1;
  const totalSteps = wizard.forecast.length;
  const postalCode = answersOf("postal").postalCode ?? "";
  const postalMatch = useMemo(() => findPostalCode(postalCode), [postalCode]);

  async function handleSubmit() {
    const { email = "", phone = "" } = answersOf("contact");
    const errors: Record<string, string> = {};
    if (!EMAIL_RE.test(email.trim())) {
      errors.email = "Indtast en gyldig emailadresse";
    }
    if (!isValidDanishNational(phone)) {
      errors.phone = "Indtast et gyldigt dansk telefonnummer (8 cifre)";
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    if (inFlight.current) return;
    inFlight.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    const startedAt = Date.now();
    let redirectStarted = false;

    try {
      const eventId = generateEventId("Lead");
      const payload = buildLeadPayload(flow, state, { utmParams, eventId, honeypot, isRepeatTask });
      const res = await fetch("/api/lead", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-PostHog-Id": posthog?.get_distinct_id?.() || "unknown",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setSubmitError(body?.error || "Noget gik galt. Prøv igen.");
        setIsSubmitting(false);
        return;
      }

      const responseBody = await parseLeadResponse(res);

      // Every accepted submission owns a token page — the match always creates
      // one, even for NO_MATCH and for a task parked for review. So a
      // missing path means the submission did NOT land, and saying "thanks"
      // here would repeat the bug this replaced: a confirmation screen for a
      // lead that does not exist.
      const thankYouPath = taskPagePath(responseBody);
      if (!thankYouPath) {
        setSubmitError("Noget gik galt. Prøv igen.");
        return;
      }

      // Conversions fire only once the submission is known to have landed - // above the check they would report a Lead for a form the customer is
      // being told to retry, and the retry mints a fresh eventId Meta can't
      // dedupe against.
      posthog?.capture(
        audience === "erhverv" ? "erhverv_lead_submitted" : "privat_lead_submitted",
        {
          audience,
          task_type: payload.taskType,
          business: payload.business,
          frequency: payload.frequency,
          square_meters: payload.squareMeters,
          // Also on wizard_started — on both events so the thank-you CTA can be
          // measured as a funnel (opened → actually submitted), not just clicks.
          is_repeat_task: isRepeatTask,
        },
      );
      if (audience) {
        trackEvent("Lead", { content_category: audience }, eventId);
      }

      await holdMatchingOverlay(startedAt, () => setMatchFound(true));
      router.push(thankYouPath);
      redirectStarted = true;
    } catch (error) {
      posthog?.captureException(error);
      setSubmitError("Noget gik galt. Prøv igen.");
    } finally {
      if (!redirectStarted) {
        inFlight.current = false;
        setIsSubmitting(false);
      }
    }
  }

  const ActiveStep = STEP_COMPONENTS[activeStepId];

  return (
    <WizardProvider
      value={{
        answersOf,
        answer,
        isBusiness,
        isRecurring: wizard.values.task?.task === "lobende",
        canGoBack: wizard.canGoBack,
        canAdvance: wizard.canAdvance,
        next,
        back: wizard.back,
        autoAdvance,
        selectAudience,
        bbrPrefilled,
        clearBbrPrefilled: (field) => setBbrPrefilled((p) => ({ ...p, [field]: false })),
        fieldErrors,
        setFieldError,
        validateEmailField,
        validatePhoneField,
        handleAddressSelect,
        handleSkipAddress,
        handleResetAddress,
        bbrLoading,
        bbrError,
        honeypot,
        setHoneypot,
        submitError,
        isSubmitting,
        handleSubmit,
        postalMatch,
      }}
    >
      <div
        ref={rootRef}
        className={
          isHero
            ? "w-full"
            : "mx-auto grid max-w-page grid-cols-1 gap-14 md:grid-cols-[1fr_280px] md:gap-14"
        }
      >
        {isSubmitting ? <MatchingOverlay isBusiness={!isPrivat} complete={matchFound} /> : null}
        <div>
          <ProgressBar
            pct={wizardPct(stepNumber, totalSteps)}
            left={STEP_PHASES[activeStepId]}
            right={`${wizardPct(stepNumber, totalSteps)}%`}
          />

          {isRepeatTask && stepNumber === 1 && (
            <PrefillNotice audience={initialAudience} postalCode={initial.postalCode ?? ""} />
          )}

          <WizardStepShell
            stepKey={stepNumber}
            direction={direction}
            stepsRef={stepsRef}
            lockStyle={lockStyle}
          >
            <ActiveStep />
          </WizardStepShell>
        </div>

        {!isHero && <WizardSidebar />}
      </div>
    </WizardProvider>
  );
}
